import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of, timer } from 'rxjs';
import { catchError, delayWhen, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { BusinessAccountService, EmployeeService, FinancialAccountService } from '@shared/services';
import { BUSINESS_TYPES, COUNTRY_CODES, MESSAGE } from '@shared/constants';
import { Address } from '@shared/models';
import { countryCodeFormat, ErrorUtils } from '@shared/utils';
import { BusinessAcccountActions, EmployeeActions, BeneficiaryActions } from './business-account.actions';
import { authFeature, businessAccountFeature, selectRouteParams, ToastActions } from '..';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import { SuccessModalComponent } from '@shared/components';
import { find, head } from 'lodash-es';

const sortedAddresses = ({ addresses, type }: { addresses?: Address[]; type: Address['type'] }) => {
  if (!addresses?.length) {
    return [];
  }

  const addressesByType = addresses
    .filter((address) => address.type === type)
    .sort((a, b) => {
      return a.primary === b.primary ? 0 : a.primary ? -1 : 1;
    });

  if (!addressesByType.length) {
    return [];
  }
  const [first, ...rest] = addressesByType;
  return [first, ...rest.map((address) => ({ ...address, primary: false }))];
};

export const loadBusinessAccountDetails$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), businessAccountService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBusinessAccountDetails),
      concatLatestFrom(() => [store.select(selectRouteParams), store.select(authFeature.selectIndustries)]),
      exhaustMap(([, routeParams, industries]) => {
        return businessAccountService.getBusinessAccountById({ businessAccountId: routeParams.businessAccountId }).pipe(
          map((businessAccountResponse) => {
            const physicalAddresses: Address[] = sortedAddresses({ addresses: businessAccountResponse.addresses, type: 'PHYSICAL' });
            const mailingAddresses: Address[] = sortedAddresses({ addresses: businessAccountResponse.addresses, type: 'MAILING' });
            const shippingAddresses: Address[] = sortedAddresses({ addresses: businessAccountResponse.addresses, type: 'SHIPPING' });

            return BusinessAcccountActions.loadBusinessAccountDetailsSuccess({
              businessAccount: {
                ...businessAccountResponse,
                legalIdCountryCode: countryCodeFormat(businessAccountResponse.legalIdCountryCode),
                physicalAddresses,
                mailingAddresses,
                shippingAddresses,
                industryLabel: industries.find((industry) => industry.value === businessAccountResponse.industryCode)?.label,
                legalEntityTypeLabel: BUSINESS_TYPES.find((businessType) => businessType.value === businessAccountResponse.legalEntityType)
                  ?.label,
                currency: COUNTRY_CODES[businessAccountResponse.legalIdCountryCode ?? 'USA'],
                businessAccountPrimaryContact:
                  find(businessAccountResponse?.contacts, ['primary', true]) ?? head(businessAccountResponse?.contacts),
              },
            });
          }),
          catchError((error) => of(BusinessAcccountActions.loadBusinessAccountDetailsFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadBusinessAccountLimits$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), businessAccountService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBusinessAccountDetails, BusinessAcccountActions.loadBusinessAccountLimits),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return businessAccountService.getBusinessAccountLimits({ businessAccountId: routeParams.businessAccountId }).pipe(
          map((businessAccountLimits) => {
            return BusinessAcccountActions.loadBusinessAccountLimitsSuccess({ businessAccountLimits });
          }),
          catchError((error) => of(BusinessAcccountActions.loadBusinessAccountLimitsFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadBusinessAccountBalances$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), businessAccountService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBusinessAccountDetails),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return businessAccountService.getBusinessAccountBalances(routeParams.businessAccountId).pipe(
          map((businessAccountBalances) => {
            return BusinessAcccountActions.loadBusinessAccountBalancesSuccess({ businessAccountBalances });
          }),
          catchError((error) => of(BusinessAcccountActions.loadBusinessAccountBalancesFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadBusinessAccountDetailsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBusinessAccountDetailsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('BusinessAcccountActions.loadBusinessAccountDetails', error);
        return ToastActions.error({ message: MESSAGE.UNKNOWN_ERROR });
      }),
    );
  },
  { functional: true },
);

export const loadBusinessAccountDetailsFailureRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBusinessAccountDetailsFailure),
      tap({
        next: () => router.navigateByUrl('/app/business-accounts'),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const loadServiceAccountDetails$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), businessAccountService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadServiceAccountDetails),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return businessAccountService.getServiceAccountById({ serviceAccountId: routeParams.serviceAccountId }).pipe(
          map((serviceAccount) => {
            return BusinessAcccountActions.loadServiceAccountDetailsSuccess({ serviceAccount });
          }),
          catchError((error) => of(BusinessAcccountActions.loadServiceAccountDetailsFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadServiceAccountDetailsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadServiceAccountDetailsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('BusinessAcccountActions.loadServiceAccountDetails', error);
        return ToastActions.error({ message: MESSAGE.UNKNOWN_ERROR });
      }),
    );
  },
  { functional: true },
);

export const loadServiceAccountDetailsFailureRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadServiceAccountDetailsFailure),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      tap({
        next: ([, routeParams]) => router.navigateByUrl(`/app/business-accounts/${routeParams.businessAccountId}/service-accounts`),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const updateServiceAccountStatus$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), businessAccountService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.updateServiceAccountStatus),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([payload, routeParams]) => {
        return businessAccountService.updateServiceAccountStatus(routeParams.serviceAccountId, payload.action).pipe(
          map(() => {
            return BusinessAcccountActions.loadServiceAccountDetails();
          }),
          catchError((error) =>
            of(BusinessAcccountActions.updateServiceAccountStatusFailure({ error: error.message, action: payload.action })),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const updateServiceAccountStatusFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.updateServiceAccountStatusFailure),
      delayWhen(() => timer(400)),
      map(({ error, action }) => {
        ErrorUtils.catchError('BusinessAcccountActions.updateServiceAccountStatusFailure', error);
        return ToastActions.error({ message: `Unable to ${action} Service Account.` });
      }),
    );
  },
  { functional: true },
);

export const deleteServiceAccount$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), businessAccountService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.deleteServiceAccount),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return businessAccountService.deleteServiceAccount(routeParams.serviceAccountId).pipe(
          map(() => {
            return BusinessAcccountActions.deleteServiceAccountSuccess();
          }),
          catchError((error) => of(BusinessAcccountActions.deleteServiceAccountFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const deleteServiceAccountSuccessRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.deleteServiceAccountSuccess),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      tap({
        next: ([, routeParams]) => router.navigateByUrl(`/app/business-accounts/${routeParams.businessAccountId}/service-accounts`),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const deleteServiceAccountFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.deleteServiceAccountFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('BusinessAcccountActions.deleteServiceAccountFailure', error);
        return ToastActions.error({ message: `Unable to delete Service Account.` });
      }),
    );
  },
  { functional: true },
);

export const loadEmployeeDetails$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), employeeService = inject(EmployeeService)) => {
    return actions$.pipe(
      ofType(EmployeeActions.loadEmployeeDetails),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return employeeService.getEmployeeDetails(routeParams.employeeId).pipe(
          map((employeeDetails) => {
            return EmployeeActions.loadEmployeeDetailsSuccess({ employeeDetails });
          }),
          catchError((error) => of(EmployeeActions.loadEmployeeDetailsFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadEmployeeDetailsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(EmployeeActions.loadEmployeeDetailsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('EmployeeActions.loadEmployeeDetailsFailure', error);
        return ToastActions.error({ message: MESSAGE.UNKNOWN_ERROR });
      }),
    );
  },
  { functional: true },
);

export const loadEmployeeDetailsFailureRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) => {
    return actions$.pipe(
      ofType(EmployeeActions.loadEmployeeRolesFailure),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      tap({
        next: ([, routeParams]) => router.navigateByUrl(`/app/business-accounts/${routeParams.businessAccountId}/users`),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const updateEmployeeStatus$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), employeeService = inject(EmployeeService), modalService = inject(ModalService)) => {
    return actions$.pipe(
      ofType(EmployeeActions.updateEmployeeStatus),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([payload, routeParams]) => {
        return employeeService.updateEmployeeStatus(routeParams.employeeId, payload.action, routeParams.businessAccountId).pipe(
          map(() => {
            const modalRef = modalService.open(SuccessModalComponent, { className: 'success-modal' });
            modalRef.componentInstance.actionName = payload.action;
            modalRef.componentInstance.type = 'User';
            return EmployeeActions.loadEmployeeDetails();
          }),
          catchError((error) => of(EmployeeActions.updateEmployeeStatusFailure({ error: error.message, action: payload.action }))),
        );
      }),
    );
  },
  { functional: true },
);

export const updateEmployeeStatusFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(EmployeeActions.updateEmployeeStatusFailure),
      delayWhen(() => timer(400)),
      map(({ error, action }) => {
        ErrorUtils.catchError('EmployeeActions.updateEmployeeStatusFailure', error);
        return ToastActions.error({ message: `Unable to ${action} User.` });
      }),
    );
  },
  { functional: true },
);

export const deleteEmployee$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), employeeService = inject(EmployeeService), modalService = inject(ModalService)) => {
    return actions$.pipe(
      ofType(EmployeeActions.deleteEmployee),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return employeeService.deleteEmployee(routeParams.employeeId, routeParams.businessAccountId).pipe(
          map(() => {
            const modalRef = modalService.open(SuccessModalComponent, { className: 'success-modal' });
            modalRef.componentInstance.actionName = 'deleted';
            modalRef.componentInstance.type = 'User';
            return EmployeeActions.deleteEmployeeSuccess();
          }),
          catchError((error) => of(EmployeeActions.deleteEmployeeFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const deleteEmployeeSuccessRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) => {
    return actions$.pipe(
      ofType(EmployeeActions.deleteEmployeeSuccess),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      tap({
        next: ([, routeParams]) => router.navigateByUrl(`/app/business-accounts/${routeParams.businessAccountId}/employees`),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const deleteEmployeeFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(EmployeeActions.deleteEmployeeFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('EmployeeActions.deleteEmployeeFailure', error);
        return ToastActions.error({ message: `Unable to delete User.` });
      }),
    );
  },
  { functional: true },
);

export const loadEmployeeRoles$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), employeeService = inject(EmployeeService)) => {
    return actions$.pipe(
      ofType(EmployeeActions.loadEmployeeRoles),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return employeeService.getEmployeeRoles(routeParams.businessAccountId).pipe(
          map((employeeRoles) => {
            return EmployeeActions.loadEmployeeRolesSuccess({ employeeRoles });
          }),
          catchError((error) => of(EmployeeActions.loadEmployeeRolesFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadEmployeeRolesFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(EmployeeActions.loadEmployeeRolesFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('EmployeeActions.loadEmployeeRolesFailure', error);
        return ToastActions.error({ message: MESSAGE.UNKNOWN_ERROR });
      }),
    );
  },
  { functional: true },
);

export const loadBusinessFinancialAccounts$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), financialAccountService = inject(FinancialAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBusinessFinancialAccounts),
      concatLatestFrom(() => [store.select(businessAccountFeature.selectBusinessFinancialAccountsFiltersRequest)]),
      mergeMap(([payload, activeFilters]) => {
        return financialAccountService.getFinancialAccounts({ ...payload.params, activeFilters }).pipe(
          map((financialAccountsList) => {
            return BusinessAcccountActions.loadBusinessFinancialAccountsSuccess({
              financialAccounts: financialAccountsList.items,
              totalElements: financialAccountsList.totalElements,
            });
          }),
          catchError((error) => {
            return of(BusinessAcccountActions.loadBusinessFinancialAccountsFailure({ error }));
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const loadBusinessFinancialAccountsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBusinessFinancialAccountsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('BusinessAcccountActions.loadBusinessFinancialAccounts', error);
        return ToastActions.error({ message: 'Unable to fetch Financial Accounts.' });
      }),
    );
  },
  { functional: true },
);

export const loadEscrowFinancialAccounts$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), financialAccountService = inject(FinancialAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadEscrowFinancialAccount),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      mergeMap(([, routeParams]) => {
        return financialAccountService.getEscrowAccount(routeParams.businessAccountId).pipe(
          map((escrowFinancialAccount) => {
            return BusinessAcccountActions.loadEscrowFinancialAccountSuccess({ escrowFinancialAccount });
          }),
          catchError((error) => {
            return of(BusinessAcccountActions.loadEscrowFinancialAccountFailure({ error }));
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const loadEscrowFinancialAccountsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadEscrowFinancialAccountFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('BusinessAcccountActions.loadEscrowFinancialAccount', error);
        return ToastActions.error({ message: 'Unable to fetch ESCROW Financial Accounts.' });
      }),
    );
  },
  { functional: true },
);

export const updateBusinessAccountState$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    businessAccountService = inject(BusinessAccountService),
    modalService = inject(ModalService),
  ) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.updateBusinessAccountState),
      concatLatestFrom(() => [store.select(selectRouteParams), store.select(businessAccountFeature.selectBusinessAccountDetails)]),
      exhaustMap(([payload, routeParams, businessAccount]) => {
        const targetState = payload.state;
        const currentState = businessAccount?.state;

        // The accountflow /state endpoint only accepts ACTIVE, PROSPECT or INACTIVE.
        // SUSPENDED is reached via the /suspend verb, and leaving SUSPENDED via /unsuspend
        // (which restores the account to ACTIVE). The modal enforces the matching invariant:
        // when currently SUSPENDED it only offers ACTIVE as a target.
        let request$;
        if (targetState === 'SUSPENDED') {
          request$ = businessAccountService.updateBusinessAccountStatus(routeParams.businessAccountId, 'suspend');
        } else if (currentState === 'SUSPENDED' && targetState === 'ACTIVE') {
          request$ = businessAccountService.updateBusinessAccountStatus(routeParams.businessAccountId, 'unsuspend');
        } else {
          request$ = businessAccountService.updateBusinessAccountState(routeParams.businessAccountId, targetState);
        }

        return request$.pipe(
          map(() => {
            const modalRef = modalService.open(SuccessModalComponent, { className: 'success-modal' });
            modalRef.componentInstance.actionName = `updated the state to ${targetState} for`;
            modalRef.componentInstance.type = 'Business Account';
            return BusinessAcccountActions.loadBusinessAccountDetails();
          }),
          catchError((error) =>
            of(
              BusinessAcccountActions.updateBusinessAccountStateFailure({
                error: ErrorUtils.parseExtendedError(error),
                state: targetState,
              }),
            ),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const updateBusinessAccountStateFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.updateBusinessAccountStateFailure),
      delayWhen(() => timer(400)),
      map(({ error, state }) => {
        ErrorUtils.catchError('BusinessAcccountActions.updateBusinessAccountStateFailure', error);
        return ToastActions.error({ message: `Unable to update Business Account state to ${state}. ${error}` });
      }),
    );
  },
  { functional: true },
);

export const loadBeneficiaries$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), businessAccountService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBeneficiaries),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return businessAccountService.getBeneficiaries(routeParams.businessAccountId).pipe(
          map((beneficiaries) => {
            return BusinessAcccountActions.loadBeneficiariesSuccess({ beneficiaries, businessAccountId: routeParams.businessAccountId });
          }),
          catchError((error) => of(BusinessAcccountActions.loadBeneficiariesFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadBeneficiariesFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BusinessAcccountActions.loadBeneficiariesFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('BusinessAcccountActions.loadBeneficiariesFailure', error);
        return ToastActions.error({ message: MESSAGE.UNKNOWN_ERROR });
      }),
    );
  },
  { functional: true },
);

export const loadBeneficiaryDetails$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), businessAccountService = inject(BusinessAccountService)) => {
    return actions$.pipe(
      ofType(BeneficiaryActions.loadBeneficiaryDetails),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return businessAccountService.getBeneficiaryById(routeParams.businessAccountId, routeParams.beneficiaryId).pipe(
          map((beneficiaryDetails) => {
            return BeneficiaryActions.loadBeneficiaryDetailsSuccess({ beneficiaryDetails });
          }),
          catchError((error) => of(BeneficiaryActions.loadBeneficiaryDetailsFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadBeneficiaryDetailsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BeneficiaryActions.loadBeneficiaryDetailsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('BeneficiaryActions.loadBeneficiaryDetailsFailure', error);
        return ToastActions.error({ message: MESSAGE.UNKNOWN_ERROR });
      }),
    );
  },
  { functional: true },
);

export const loadBeneficiaryDetailsFailureRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) => {
    return actions$.pipe(
      ofType(BeneficiaryActions.loadBeneficiaryDetailsFailure),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      tap({
        next: ([, routeParams]) => router.navigateByUrl(`/app/business-accounts/${routeParams.businessAccountId}/beneficiaries`),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const updateBeneficiaryStatus$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    businessAccountService = inject(BusinessAccountService),
    modalService = inject(ModalService),
  ) => {
    return actions$.pipe(
      ofType(BeneficiaryActions.updateBeneficiaryStatus),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([payload, routeParams]) => {
        return businessAccountService
          .updateBeneficiaryStatus(routeParams.beneficiaryId, payload.action, routeParams.businessAccountId)
          .pipe(
            map(() => {
              const modalRef = modalService.open(SuccessModalComponent, { className: 'success-modal' });
              modalRef.componentInstance.actionName = payload.action;
              modalRef.componentInstance.type = 'Beneficial Owner';
              return BeneficiaryActions.loadBeneficiaryDetails();
            }),
            catchError((error) => of(BeneficiaryActions.updateBeneficiaryStatusFailure({ error: error.message, action: payload.action }))),
          );
      }),
    );
  },
  { functional: true },
);

export const updateBeneficiaryStatusFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BeneficiaryActions.updateBeneficiaryStatusFailure),
      delayWhen(() => timer(400)),
      map(({ error, action }) => {
        ErrorUtils.catchError('BeneficiaryActions.updateBeneficiaryStatusFailure', error);
        return ToastActions.error({ message: `Unable to ${action} Beneficial Owner.` });
      }),
    );
  },
  { functional: true },
);

export const deleteBeneficiary$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    businessAccountService = inject(BusinessAccountService),
    modalService = inject(ModalService),
  ) => {
    return actions$.pipe(
      ofType(BeneficiaryActions.deleteBeneficiary),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return businessAccountService.deleteBeneficiary(routeParams.beneficiaryId, routeParams.businessAccountId).pipe(
          map(() => {
            const modalRef = modalService.open(SuccessModalComponent, { className: 'success-modal' });
            modalRef.componentInstance.actionName = 'deleted';
            modalRef.componentInstance.type = 'Beneficial Owner';
            return BeneficiaryActions.deleteBeneficiarySuccess();
          }),
          catchError((error) => of(BeneficiaryActions.deleteBeneficiaryFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const deleteBeneficiarySuccessRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) => {
    return actions$.pipe(
      ofType(BeneficiaryActions.deleteBeneficiarySuccess),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      tap({
        next: ([, routeParams]) => router.navigateByUrl(`/app/business-accounts/${routeParams.businessAccountId}/beneficiaries`),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const deleteBeneficiaryFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(BeneficiaryActions.deleteBeneficiaryFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('BeneficiaryActions.deleteBeneficiaryFailure', error);
        return ToastActions.error({ message: `Unable to delete Beneficial Owner.` });
      }),
    );
  },
  { functional: true },
);
